import Button from "@/components/Button";
import SimpleDropdown from "@/components/dropdowns/SimpleDropdown/SimpleDropdown";
import Toolbar from "@/containers/Toolbar";
import { favorite, filterIcon, shareIcon } from "@/designSystemIcons";
import { TacticsSidePanelOption } from "@/features/Tactics/TacticsSidePanel";
import {
  TacticSortOption,
  TacticSortOptionValues,
} from "@/redux/tactics/tacticsSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import Oval from "react-loading-icons/dist/esm/components/oval";
import { useMediaQuery } from "usehooks-ts";

interface TacticToolbarProps {
  isFavoriteFilterOn: boolean;
  panelState: TacticsSidePanelOption;
  currentSortBy: TacticSortOption;
  loading: boolean;
  canUploadTactic: boolean;
  handleFiltersClicked: () => void;
  handleFavoriteClicked: () => void;
  handleSortByChanged: (option: TacticSortOption) => void;
  handleUploadTacticsClicked: () => void;
}

const TACTIC_SORT_KEYS = {
  recommended: "sort.recommended",
  "date-added": "sort.dateAdded",
  "popularity-score": "sort.popularityScore",
} as const satisfies Record<TacticSortOptionValues, string>;

const TacticToolbar = ({
  isFavoriteFilterOn,
  panelState,
  currentSortBy,
  loading,
  canUploadTactic,
  handleFiltersClicked,
  handleFavoriteClicked,
  handleSortByChanged,
  handleUploadTacticsClicked,
}: TacticToolbarProps) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const { t } = useTranslation(["tactics", "common"]);
  const sortOptions = (
    Object.keys(TACTIC_SORT_KEYS) as TacticSortOptionValues[]
  ).map((value) => ({ value, label: t(TACTIC_SORT_KEYS[value]) }));
  return (
    <Toolbar>
      <div className="flex flex-row justify-between w-full">
        <div className="flex flex-row gap-x-2 items-center">
          <Button
            variant="tertiary"
            label={t("common:filters")}
            active={panelState === "filters"}
            icon={<div className="w-5 h-5">{filterIcon}</div>}
            onClick={handleFiltersClicked}
            isMobileToolbarButton={isMobile}
          />
          <div className="h-6 w-[1px] bg-new-gray-750 my-auto hidden touch-device:block" />
          {!isMobile && (
            <Button
              variant="tertiary"
              label={t("common:favorites")}
              active={isFavoriteFilterOn}
              icon={<div className="w-4 h-4">{favorite}</div>}
              onClick={(e) => {
                e.stopPropagation();
                handleFavoriteClicked();
              }}
            />
          )}
          {canUploadTactic && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleUploadTacticsClicked();
              }}
              variant="tertiary"
              label={t("toolbar.uploadNewTactic")}
              icon={<div className="w-5 h-5">{shareIcon}</div>}
            />
          )}
          {loading && <Oval width={20} height={20} />}
        </div>
        <div className="flex flex-row items-center text-sm gap-x-2 text-dark-tertiary">
          <span className="hidden desktop:block">{t("common:sortBy")}</span>
          <SimpleDropdown
            variant="tertiary"
            options={sortOptions}
            selectedOption={sortOptions.find(
              (option) => option.value === currentSortBy.value,
            )}
            onChange={(option) =>
              handleSortByChanged({
                value: option.value as TacticSortOptionValues,
              })
            }
            align="right"
          />
        </div>
      </div>
    </Toolbar>
  );
};

export default TacticToolbar;
